"use client"
import { createContext, useContext, useState, type ReactNode } from "react"
import {
  HoverSwapFadeOverlay,
  hoverSwapHiddenContentStyle,
  useHoverSwapSequentialFade,
} from "../hoverSwapFade"
import { useHoverSwapsDisabled } from "../hoverSwapsDisabled"
import {
  HudHoverDescription,
  HudHoverDetailBody,
  HudHoverTitle,
} from "../hudHoverDetailHeader"

export type HoverableMetricId =
  | "employment"
  | "alignmentEffort"
  | "medianIncome"
  | "safetyTax"
  // Tab 74's stat-row pair (CapabilityRiskStats.tsx) — the graph hover's two
  // numbers promoted to the data bar, replacing Safety Researchers / Total
  // Slowdown there.
  | "rdSpeedup"
  | "takeoverRisk"

// ─── Editable content ────────────────────────────────────────────────────────
// Title + layperson definition per HUD metric, shown by the in-canvas metric
// hover takeover. Copy mirrors the indicators' floating tooltips
// (EmploymentRate.tsx etc.) — keep the two in sync when editing.
export const METRIC_HOVER_DEFINITIONS: Record<
  HoverableMetricId,
  { title: string; definition: ReactNode }
> = {
  employment: {
    title: "Employment Rate",
    definition: <div>
  <p>Percentage of Americans who have jobs.</p>
  <p className="mt-2">Note: We don't use "unemployment rate" because it excludes people who aren't actively seeking work, so it understates job losses as AI displaces workers.</p>
    </div>,
  },
  alignmentEffort: {
    // Renamed to match main's HUD item name (2026-07-07).
    title: "Safety Researchers",
    definition:
      "Estimated researchers focused primarily on technical alignment work with access to frontier models.",
  },
  medianIncome: {
    title: "Median Income",
    definition: "Median income across the US working age population, in 2025 USD.",
  },
  safetyTax: {
    // Renamed to match main's HUD item name (2026-07-07).
    title: "Total Slowdown",
    definition:
      "Total delay in AI capability scaling relative to status quo scaling, for safety reasons.",
  },
  // Tab 74's pair — DRAFT copy, edit freely. Wording follows the combined
  // graph hover's definitions (CELLS_DEF_COLUMNS_COPY /
  // COMBINED_CURRENT_LEVEL_RISK_COPY, capabilityHoverSwap.tsx).
  rdSpeedup: {
    title: "R&D Speedup",
    definition:
      "How much faster AI research and development moves with AI doing the work, compared to human researchers alone.",
  },
  takeoverRisk: {
    title: "Takeover Risk",
    definition:
      "How likely AI at its current capability level is to escape human control, given this year's alignment techniques.",
  },
}

// Tab 3 — the hovered stat-row metric inverts into a red chip: a rounded
// square painted behind the label + value, standard red, white text. All
// four chips are the SAME height — the stat row stretches its items, so each
// chip spans the row's full band (see MetricTrigger for the geometry).
export const METRIC_TRIGGER_RED_CHIP_STYLE = {
  background: "var(--accent)",
  textColor: "#fff",
  borderRadiusPx: 4,
  paddingXPx: 6,
  paddingYPx: 4,
}

// Trims the stat label's block to end exactly at its last line's TEXT
// BASELINE (text-box-trim), instead of at the line box's invisible
// descender/leading slack. The swap row pins the labels' bottom to the power
// treemap's bottom edge — with the trim, that alignment is exact on every
// font/platform rather than depending on one font's metrics. Browsers
// without text-box support ignore the property and take the stats row's
// eyeballed fallback margin instead (see the supports-gated mb pair on the
// row in variants.tsx — the two must stay in sync).
export const METRIC_LABEL_BASELINE_TRIM_CLASS =
  "[text-box:trim-end_cap_alphabetic]"

// How the hovered metric's own trigger (the stat-row label + value) marks
// itself while its detail is showing. undefined = no marking (the detail
// alone signals the hover); "red-chip" = METRIC_TRIGGER_RED_CHIP_STYLE.
export type MetricTriggerHighlight = "red-chip"

// What a hovering indicator reports: which metric, plus its currently
// displayed value already formatted ("62%", "$45K", "2 yrs") — snapshotted at
// hover time so the detail can lead with it without re-deriving the number.
export type HoveredMetric = { id: HoverableMetricId; valueText: string }

type MetricHoverSwapContextValue = {
  hoveredMetric: HoveredMetric | null
  setHoveredMetric: (metric: HoveredMetric | null) => void
  triggerHighlight?: MetricTriggerHighlight
}

const MetricHoverSwapContext = createContext<MetricHoverSwapContextValue | null>(null)

// Null outside a <MetricHoverSwap>. The four metric indicators (EmploymentRate,
// AlignmentResearcherCount, MedianIncome, SafetyTaxIndicator) read this to
// decide whether hover should open their floating tooltip (no provider) or be
// reported here so a MetricHoverSwapTarget can render the definition in-canvas.
export const useMetricHoverSwap = () => useContext(MetricHoverSwapContext)

// Renders no DOM of its own, so wrapping existing flex/grid children does not
// disturb their layout relationship to the parent container.
export const MetricHoverSwap = ({
  triggerHighlight,
  children,
}: {
  triggerHighlight?: MetricTriggerHighlight
  children: ReactNode
}) => {
  const [hoveredMetric, setHoveredMetric] = useState<HoveredMetric | null>(null)
  // Hover is not an input on this surface (mobile dashboard) — provide
  // null so every consumer renders its base, unhovered content. (Taps to
  // the detail pages live in MetricTrigger's click path, not here.)
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  return (
    <MetricHoverSwapContext.Provider
      value={hoverSwapsDisabled ? null : { hoveredMetric, setHoveredMetric, triggerHighlight }}
    >
      {children}
    </MetricHoverSwapContext.Provider>
  )
}

// The hovered metric's detail block: bold mono title, sans definition, and
// the current value set off beneath. Used by MetricHoverSwapTarget below and
// by the below-the-HUD detail slot (belowHudDetailSwap.tsx) — the latter
// passes hideValue, since there the stat row (with the live number) stays
// visible right above the detail and repeating it reads redundant.
export const MetricHoverDetailContent = ({
  hoveredMetric,
  hideValue = false,
}: {
  hoveredMetric: HoveredMetric
  hideValue?: boolean
}) => {
  const definition = METRIC_HOVER_DEFINITIONS[hoveredMetric.id]
  return (
    <>
      <HudHoverTitle>{definition.title}</HudHoverTitle>
      <HudHoverDescription>{definition.definition}</HudHoverDescription>
      {/* The metric's current value ("62%") sits below the definition. */}
      {!hideValue && (
        <HudHoverDetailBody className="font-mono font-bold text-[11px]">
          {hoveredMetric.valueText}
        </HudHoverDetailBody>
      )}
    </>
  )
}

// Wraps the HUD block whose spot the hovered metric's definition takes over
// (the dist-of-power treemap). Children keep their layout footprint but go
// visibility-hidden while a metric is hovered; the definition paints over
// them and the canvas never reflows.
export const MetricHoverSwapTarget = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const swap = useMetricHoverSwap()
  const sequentialFade = useHoverSwapSequentialFade()
  const hoveredMetric = swap?.hoveredMetric ?? null
  const definition = hoveredMetric ? METRIC_HOVER_DEFINITIONS[hoveredMetric.id] : null
  return (
    <div className={`relative ${className ?? ""}`}>
      {/* Hidden via opacity (not visibility) so the treemap underneath stays
          HIT-TESTABLE while the definition covers it — see the same pattern
          in PolicyHoverSwapTarget. Undefined when idle so an outer swap
          target's hiding still inherits. */}
      <div style={hoverSwapHiddenContentStyle(Boolean(definition), sequentialFade)}>
        {children}
      </div>
      {/* pointer-events-none: the definition never intercepts the pointer. */}
      <HoverSwapFadeOverlay
        active={Boolean(definition && hoveredMetric)}
        contentKey={hoveredMetric?.id}
        className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
      >
        {hoveredMetric && <MetricHoverDetailContent hoveredMetric={hoveredMetric} />}
      </HoverSwapFadeOverlay>
    </div>
  )
}
